import { Clock3, Loader2, X } from "lucide-react"
import { DatePicker } from "modules/permit/components/DatePicker"
import { Button } from "shared/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Textarea } from "shared/ui/textarea"
import type { ExtendPermitFieldErrors } from "../hooks/useExtendPermitFlow"

const TIME_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, "0"))
const TIME_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) => (index * 5).toString().padStart(2, "0"))

interface ExtendPermitDialogProps {
  open: boolean
  permitNumber?: string | null
  currentStartDate: string
  currentStartTime: string
  currentEndDate: string
  currentEndTime: string
  isSubmitting: boolean
  endDate: string
  endHour: string
  endMinute: string
  reason: string
  error?: string | null
  fieldErrors?: ExtendPermitFieldErrors
  onOpenChange: (open: boolean) => void
  onEndDateChange: (value: string) => void
  onEndHourChange: (value: string) => void
  onEndMinuteChange: (value: string) => void
  onReasonChange: (value: string) => void
  onSubmit: () => void
}

export function ExtendPermitDialog({
  open,
  permitNumber,
  currentStartDate,
  currentStartTime,
  currentEndDate,
  currentEndTime,
  isSubmitting,
  endDate,
  endHour,
  endMinute,
  reason,
  error,
  fieldErrors,
  onOpenChange,
  onEndDateChange,
  onEndHourChange,
  onEndMinuteChange,
  onReasonChange,
  onSubmit,
}: ExtendPermitDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0" showCloseButton={false}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <DialogHeader className="space-y-1 text-left">
            <DialogTitle className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
              <Clock3 className="h-5 w-5 text-blue-500" />
              {"\u0e15\u0e48\u0e2d Permit"}
            </DialogTitle>
            <p className="text-sm text-slate-500">
              {permitNumber
                ? `${"\u0e2a\u0e33\u0e2b\u0e23\u0e31\u0e1a"} ${permitNumber}`
                : "\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48\u0e41\u0e25\u0e30\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e43\u0e19\u0e01\u0e32\u0e23\u0e15\u0e48\u0e2d Permit"}
            </p>
          </DialogHeader>
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full text-slate-500 hover:bg-slate-100"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-6 px-6 py-6">
          <div className="rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-blue-900">
            {currentStartDate ? (
              <p className="font-medium">
                {"\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"}: {currentStartDate} {currentStartTime || ""}
              </p>
            ) : null}
            <p className={`font-medium ${currentStartDate ? "mt-1" : ""}`}>
              {"\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e40\u0e14\u0e34\u0e21"}: {currentEndDate || "-"} {currentEndTime || ""}
            </p>
            <p className="mt-1 text-blue-700">
              {"\u0e2a\u0e32\u0e21\u0e32\u0e23\u0e16\u0e15\u0e48\u0e2d Permit \u0e44\u0e14\u0e49\u0e44\u0e21\u0e48\u0e40\u0e01\u0e34\u0e19 24 \u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07\u0e08\u0e32\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e40\u0e23\u0e34\u0e48\u0e21\u0e15\u0e49\u0e19"}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="extend-permit-end-date" className="text-sm font-semibold text-slate-900">
              {"\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48"} <span className="text-rose-500">*</span>
            </Label>
            <DatePicker
              id="extend-permit-end-date"
              value={endDate}
              onChange={onEndDateChange}
              disabled={isSubmitting}
              placeholder={"\u0e40\u0e25\u0e37\u0e2d\u0e01\u0e27\u0e31\u0e19\u0e17\u0e35\u0e48\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48"}
              error={fieldErrors?.endDate}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-semibold text-slate-900">
              {"\u0e40\u0e27\u0e25\u0e32\u0e2a\u0e34\u0e49\u0e19\u0e2a\u0e38\u0e14\u0e43\u0e2b\u0e21\u0e48"} <span className="text-rose-500">*</span>
            </Label>
            <div className="flex items-center gap-2">
              <Select value={endHour || undefined} onValueChange={onEndHourChange} disabled={isSubmitting}>
                <SelectTrigger className="border-gray-200 bg-gray-50">
                  <SelectValue placeholder={"\u0e0a\u0e31\u0e48\u0e27\u0e42\u0e21\u0e07"} />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {TIME_HOUR_OPTIONS.map((option) => (
                    <SelectItem key={`extend-end-hour-${option}`} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-gray-400">:</span>
              <Select
                value={endMinute || undefined}
                onValueChange={onEndMinuteChange}
                disabled={isSubmitting || !endHour}
              >
                <SelectTrigger className="border-gray-200 bg-gray-50">
                  <SelectValue placeholder={"\u0e19\u0e32\u0e17\u0e35"} />
                </SelectTrigger>
                <SelectContent className="max-h-60 overflow-y-auto">
                  {TIME_MINUTE_OPTIONS.map((option) => (
                    <SelectItem key={`extend-end-minute-${option}`} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {fieldErrors?.endTime ? <p className="text-sm text-rose-600">{fieldErrors.endTime}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="extend-permit-reason" className="text-sm font-semibold text-slate-900">
              {"\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e43\u0e19\u0e01\u0e32\u0e23\u0e15\u0e48\u0e2d Permit"} <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="extend-permit-reason"
              rows={4}
              className={`min-h-[120px] resize-none rounded-2xl text-base placeholder:text-slate-400 ${
                fieldErrors?.reason ? "border-rose-500 focus-visible:ring-rose-500" : "border-slate-300"
              }`}
              placeholder={
                "\u0e23\u0e30\u0e1a\u0e38\u0e40\u0e2b\u0e15\u0e38\u0e1c\u0e25\u0e17\u0e35\u0e48\u0e15\u0e49\u0e2d\u0e07\u0e01\u0e32\u0e23\u0e02\u0e22\u0e32\u0e22\u0e40\u0e27\u0e25\u0e32\u0e01\u0e32\u0e23\u0e17\u0e33\u0e07\u0e32\u0e19 \u0e40\u0e0a\u0e48\u0e19 \u0e07\u0e32\u0e19\u0e22\u0e31\u0e07\u0e44\u0e21\u0e48\u0e41\u0e25\u0e49\u0e27\u0e40\u0e2a\u0e23\u0e47\u0e08\u0e15\u0e32\u0e21\u0e41\u0e1c\u0e19..."
              }
              value={reason}
              onChange={(event) => onReasonChange(event.target.value)}
              disabled={isSubmitting}
            />
            {fieldErrors?.reason ? <p className="text-sm text-rose-600">{fieldErrors.reason}</p> : null}
          </div>

          {error ? <p className="text-sm text-rose-600">{error}</p> : null}
        </div>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {"\u0e22\u0e01\u0e40\u0e25\u0e34\u0e01"}
          </Button>
          <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Clock3 className="mr-2 h-4 w-4" />}
            {"\u0e22\u0e37\u0e19\u0e22\u0e31\u0e19\u0e01\u0e32\u0e23\u0e15\u0e48\u0e2d Permit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
